import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Editorial form primitives. Hairline borders, no shadow on focus.
 * Labels sit above; help text and errors sit below in muted-foreground.
 */

export function TextField({
  id, label, type = 'text', placeholder, value, onChange, autoComplete, autoFocus, required,
  inputMode,
}: {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  inputMode?: 'email' | 'text' | 'numeric';
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] font-medium text-muted-foreground">{label}</span>
      <input
        id={id}
        name={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required={required}
        inputMode={inputMode}
        className="mt-1.5 block w-full h-11 rounded-md border border-border bg-card px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-foreground/15 focus:border-foreground/30 placeholder:text-muted-foreground/60"
      />
    </label>
  );
}

export function PasswordField({
  id, label, value, onChange, autoComplete = 'current-password', showStrength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  showStrength?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <label htmlFor={id} className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] font-medium text-muted-foreground">{label}</span>
      <div className="mt-1.5 relative">
        <input
          id={id}
          name={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="block w-full h-11 rounded-md border border-border bg-card pl-3 pr-10 text-[14px] focus:outline-none focus:ring-2 focus:ring-foreground/15 focus:border-foreground/30"
        />
        <button
          type="button"
          aria-label={show ? 'hide password' : 'show password'}
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {showStrength && <PasswordStrengthMeter value={value} />}
    </label>
  );
}

function scoreStrength(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string } {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (pw.length >= 14) s = Math.max(s, 4);
  const labels = ['empty', 'weak', 'fair', 'good', 'strong'] as const;
  return { score: s as 0 | 1 | 2 | 3 | 4, label: labels[s] };
}

function PasswordStrengthMeter({ value }: { value: string }) {
  const { t } = useTranslation();
  const { score } = scoreStrength(value);
  if (!value) return null;
  const colors = [
    'bg-ink-200',
    'bg-rose-400',
    'bg-amber-400',
    'bg-emerald-400',
    'bg-emerald-600',
  ];
  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex-1 grid grid-cols-4 gap-1">
        {[1, 2, 3, 4].map((n) => (
          <span
            key={n}
            className={`h-1 rounded-full ${score >= n ? colors[score] : 'bg-ink-200'}`}
          />
        ))}
      </div>
      <span className="num text-[10px] uppercase tracking-[0.14em] text-muted-foreground w-12 text-right">
        {t(`auth.strength_${['empty','weak','fair','good','strong'][score]}`)}
      </span>
    </div>
  );
}

export function OrDivider({ label }: { label: string }) {
  return (
    <div className="my-5 flex items-center gap-3">
      <span className="flex-1 h-px bg-border" />
      <span className="text-[10.5px] uppercase tracking-[0.18em] font-medium text-muted-foreground">{label}</span>
      <span className="flex-1 h-px bg-border" />
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
    >
      {message}
    </div>
  );
}

export function FormSuccess({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="status"
      className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800"
    >
      {message}
    </div>
  );
}

export function SubmitButton({
  pending, children,
}: {
  pending: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="w-full h-11 rounded-md bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      {pending ? '…' : children}
    </button>
  );
}
